import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

interface SpendData {
  name: string;
  value: number;
  percentage: number;
  [key: string]: any;
}

interface ShareOfSpendProps {
  data: SpendData[] | undefined;
  title?: string;
  useBarChart?: boolean;
}

const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1'];

export const ShareOfSpend: React.FC<ShareOfSpendProps> = ({
  data,
  title = 'Share of Total Spend by Channel',
  useBarChart = false,
}) => {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="w-full h-full bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-96 bg-gray-50 rounded">
          <p className="text-gray-500">No spend data available from model results</p>
        </div>
      </div>
    );
  }

  const chartData = data;
  const totalSpend = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="w-full h-full bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>

      {!useBarChart ? (
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData as any}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }: any) => `${name} ${percentage.toFixed(1)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: any) => [`$${value.toLocaleString()}`, 'Spend']}
                  contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #ccc' }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex-1">
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900">Spend Breakdown</h4>
              {chartData.map((item, idx) => (
                <div key={item.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: colors[idx % colors.length] }}
                    />
                    <span className="text-sm text-gray-700">{item.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900">
                      ${item.value.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-600">{item.percentage.toFixed(1)}%</div>
                  </div>
                </div>
              ))}
              <div className="pt-2 border-t border-gray-200 flex justify-between">
                <span className="font-semibold text-gray-900">Total</span>
                <span className="font-semibold text-gray-900">${totalSpend.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fontSize: 12 }}
            />
            <YAxis label={{ value: 'Spend ($)', angle: -90, position: 'insideLeft' }} />
            <Tooltip
              formatter={(value: any) => `$${value.toLocaleString()}`}
              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #ccc' }}
            />
            <Bar dataKey="value" fill="#8884d8" radius={[8, 8, 0, 0]}>
              {chartData.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}

      <div className="mt-4 text-sm text-gray-600">
        <p>
          <strong>Interpretation:</strong> Shows how your total marketing budget is allocated across channels.
          Use this to compare your current spend distribution against what the model suggests for optimal ROI.
          Consider whether high-spend channels are delivering proportional returns.
        </p>
      </div>
    </div>
  );
};
